import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Phone, MapPin, Clock, Star, ArrowRight, Paintbrush, Home, Building2, Palette, Calendar, Shield, Users } from 'lucide-react';
import { cities, services } from '../data/seoData';
import { getLocationContent } from '../data/seoContent';

// SEO Landing Page Component for City/District pages
export default function SeoLandingPage() {
  const location = useLocation();
  
  // Extract city and district from URL path
  const fullSlug = location.pathname.replace('/maler-', '');
  
  // Try to find the city by checking if the slug starts with a city slug
  let city = null;
  let district = null;
  
  // Sort cities by slug length (longest first) to match correctly
  const sortedCities = Object.values(cities).sort((a, b) => b.slug.length - a.slug.length);
  
  for (const cityData of sortedCities) {
    if (fullSlug === cityData.slug || fullSlug.startsWith(cityData.slug + '-')) {
      city = cityData;
      // Check if there's a district after the city slug
      if (fullSlug.length > cityData.slug.length) {
        const districtSlug = fullSlug.slice(cityData.slug.length + 1);
        district = city.districts.find(d => d.slug === districtSlug);
      }
      break;
    }
  }
  
  if (!city) {
    return <NotFoundContent />;
  }
  
  const locationName = district ? `${city.name}-${district.name}` : city.name;
  const locationSlug = district ? `${city.slug}-${district.slug}` : city.slug;
  
  // Get unique content for this location
  const districtKey = district ? district.slug : null;
  const content = getLocationContent(city.slug, districtKey);
  
  // Use content or fallback to generic
  const heroTitle = content?.heroTitle || `Ihr Maler in ${locationName}`;
  const heroSubtitle = content?.heroSubtitle || `Professionelle Malerarbeiten für Privat und Gewerbe. Innenanstrich, Fassadenarbeiten, Tapezieren und mehr – alles aus einer Hand von FarbWelt Service.`;
  const mainText = content?.mainContent || null;
  const faqItems = content?.faq || getDefaultFaq(locationName, city.name);
  const serviceHighlights = content?.serviceHighlights || getDefaultServiceHighlights();
  const nearbyAreas = content?.nearbyAreas || [];
  const metaInfo = content?.meta || {
    title: `Maler in ${locationName} | FarbWelt Service - Ihr Malerbetrieb`,
    description: `Professionelle Malerarbeiten in ${locationName}. ✓ Innenanstrich ✓ Fassadenarbeiten ✓ Tapezieren ✓ Kostenlose Beratung. Jetzt Termin buchen!`,
    keywords: `Maler ${locationName}, Malerarbeiten ${city.name}, Malerbetrieb ${locationName}, Anstreicher ${city.name}`
  };

  // Structured Data for this page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `FarbWelt Service - Maler ${locationName}`,
    "description": metaInfo.description,
    "url": `https://www.farbwelt-service.de/maler-${locationSlug}`,
    "telephone": "+49-2133-7787688",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city.name,
      "addressRegion": "Nordrhein-Westfalen",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": city.coordinates.lat,
      "longitude": city.coordinates.lng
    },
    "areaServed": {
      "@type": "Place",
      "name": locationName
    },
    "priceRange": "€€",
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "18:00"
    }
  };

  // FAQ Schema
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{metaInfo.title}</title>
        <meta name="description" content={metaInfo.description} />
        <meta name="keywords" content={metaInfo.keywords} />
        <link rel="canonical" href={`https://www.farbwelt-service.de/maler-${locationSlug}`} />
        <meta property="og:title" content={metaInfo.title} />
        <meta property="og:description" content={metaInfo.description} />
        <meta property="og:url" content={`https://www.farbwelt-service.de/maler-${locationSlug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaInfo.title} />
        <meta name="twitter:description" content={metaInfo.description} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#2E2A88] via-[#1e1a5e] to-[#9B1B30] text-white py-20 lg:py-28">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            {/* Breadcrumb */}
            <nav className="text-sm mb-6 text-white/80" aria-label="Breadcrumb">
              <ol className="flex items-center flex-wrap" itemScope itemType="https://schema.org/BreadcrumbList">
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to="/" className="hover:text-white" itemProp="item">
                    <span itemProp="name">Home</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <span className="mx-2">/</span>
                <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                  <Link to={`/maler-${city.slug}`} className="hover:text-white" itemProp="item">
                    <span itemProp="name">Maler {city.name}</span>
                  </Link>
                  <meta itemProp="position" content="2" />
                </li>
                {district && (
                  <>
                    <span className="mx-2">/</span>
                    <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                      <span itemProp="name">{district.name}</span>
                      <meta itemProp="position" content="3" />
                    </li>
                  </>
                )}
              </ol>
            </nav>
            
            <div className="max-w-4xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-barlow mb-6 leading-tight">
                {heroTitle}
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 font-manrope leading-relaxed">
                {heroSubtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/termin" data-testid="hero-cta-beratung">
                  <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] text-white px-8 py-6 text-lg w-full sm:w-auto">
                    <Phone className="w-5 h-5 mr-2" />
                    Kostenlose Beratung
                  </Button>
                </Link>
                <Link to="/termin" data-testid="hero-cta-termin">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto">
                    Termin buchen
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="bg-gray-50 py-8 border-b">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Star className="w-8 h-8 text-[#F59E0B] mb-2" />
                <span className="font-bold text-lg">Top Bewertungen</span>
                <span className="text-sm text-gray-600">Zufriedene Kunden</span>
              </div>
              <div className="flex flex-col items-center">
                <Clock className="w-8 h-8 text-[#9B1B30] mb-2" />
                <span className="font-bold text-lg">Termingerecht</span>
                <span className="text-sm text-gray-600">Pünktliche Ausführung</span>
              </div>
              <div className="flex flex-col items-center">
                <Shield className="w-8 h-8 text-green-600 mb-2" />
                <span className="font-bold text-lg">Festpreise</span>
                <span className="text-sm text-gray-600">Keine versteckten Kosten</span>
              </div>
              <div className="flex flex-col items-center">
                <MapPin className="w-8 h-8 text-[#2E2A88] mb-2" />
                <span className="font-bold text-lg">Vor Ort in {city.name}</span>
                <span className="text-sm text-gray-600">Kurze Anfahrtswege</span>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Section - Unique Text */}
        {mainText && (
          <section className="py-16 lg:py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <article className="prose prose-lg max-w-none text-gray-700">
                  {/* Intro */}
                  {mainText.intro && (
                    <div className="mb-8">
                      {mainText.intro.split('\n\n').map((paragraph, idx) => (
                        <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                      ))}
                    </div>
                  )}

                  {/* Local Expertise */}
                  {mainText.localExpertise && (
                    <>
                      <h2 className="text-2xl md:text-3xl font-bold font-barlow text-gray-900 mt-12 mb-6">
                        Lokale Expertise in {locationName}
                      </h2>
                      <div className="mb-8">
                        {mainText.localExpertise.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Services */}
                  {mainText.services && (
                    <>
                      <h2 className="text-2xl md:text-3xl font-bold font-barlow text-gray-900 mt-12 mb-6">
                        Unsere Leistungen in {locationName}
                      </h2>
                      <div className="mb-8">
                        {mainText.services.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Process */}
                  {mainText.process && (
                    <>
                      <h2 className="text-2xl md:text-3xl font-bold font-barlow text-gray-900 mt-12 mb-6">
                        So arbeiten wir
                      </h2>
                      <div className="mb-8">
                        {mainText.process.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Why Us */}
                  {mainText.whyUs && (
                    <>
                      <h2 className="text-2xl md:text-3xl font-bold font-barlow text-gray-900 mt-12 mb-6">
                        Warum FarbWelt Service wählen?
                      </h2>
                      <div className="mb-8">
                        {mainText.whyUs.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="mb-4 leading-relaxed">{paragraph.trim()}</p>
                        ))}
                      </div>
                    </>
                  )}
                </article>
              </div>
            </div>
          </section>
        )}

        {/* Services Section */}
        <section className="py-16 lg:py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-barlow text-gray-900 mb-4">
                {mainText ? 'Alle Leistungen im Überblick' : `Unsere Leistungen in ${locationName}`}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Von Innenanstrich bis Fassadensanierung – wir bieten das komplette Spektrum 
                professioneller Malerarbeiten für Ihr Zuhause oder Gewerbe.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Object.values(services).slice(0, 6).map((service) => (
                <Card key={service.slug} className="hover:shadow-lg transition-shadow border-0 shadow-md bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-lg flex items-center justify-center mb-4">
                      {service.slug === 'innenanstrich' && <Home className="w-6 h-6 text-[#9B1B30]" />}
                      {service.slug === 'fassadenarbeiten' && <Building2 className="w-6 h-6 text-[#9B1B30]" />}
                      {service.slug === 'tapezierarbeiten' && <Palette className="w-6 h-6 text-[#9B1B30]" />}
                      {service.slug === 'lackierarbeiten' && <Paintbrush className="w-6 h-6 text-[#9B1B30]" />}
                      {service.slug === 'renovierung' && <Users className="w-6 h-6 text-[#9B1B30]" />}
                      {service.slug === 'wohnungsuebergabe' && <Calendar className="w-6 h-6 text-[#9B1B30]" />}
                      {!['innenanstrich', 'fassadenarbeiten', 'tapezierarbeiten', 'lackierarbeiten', 'renovierung', 'wohnungsuebergabe'].includes(service.slug) && 
                        <Paintbrush className="w-6 h-6 text-[#9B1B30]" />}
                    </div>
                    <h3 className="text-xl font-bold font-barlow mb-2">{service.name}</h3>
                    <p className="text-gray-600 mb-4">{service.shortDescription}</p>
                    <ul className="space-y-2 mb-4">
                      {service.benefits.slice(0, 3).map((benefit, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                    <Link 
                      to={`/${service.slug}-${city.slug}`} 
                      className="text-[#9B1B30] font-medium hover:underline inline-flex items-center"
                      data-testid={`service-link-${service.slug}`}
                    >
                      {service.name} in {city.name} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Highlights (if available) */}
        {serviceHighlights && serviceHighlights.length > 0 && (
          <section className="py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
                Beliebteste Leistungen in {locationName}
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {serviceHighlights.map((highlight, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                    <h3 className="text-xl font-bold mb-2">{highlight.title}</h3>
                    <p className="text-gray-600 mb-4">{highlight.description}</p>
                    <Link to={highlight.link} className="text-[#9B1B30] font-medium hover:underline inline-flex items-center">
                      {highlight.title} in {city.name} <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* About Location Section (fallback if no unique content) */}
        {!mainText && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold font-barlow mb-6">
                  Malerarbeiten in {locationName} – Qualität vom Fachbetrieb
                </h2>
                <div className="prose prose-lg max-w-none text-gray-700">
                  <p>{city.description}</p>
                  <p>
                    Als erfahrener Malerbetrieb sind wir Ihr kompetenter Ansprechpartner für alle 
                    Malerarbeiten in {locationName}. Ob Sie Ihre Wohnung renovieren möchten, 
                    einen neuen Anstrich für Ihr Büro benötigen oder die Fassade Ihres Hauses 
                    auffrischen lassen wollen – wir sind für Sie da.
                  </p>
                  <p>
                    Unser Team aus qualifizierten Malern arbeitet sauber, termingerecht und 
                    zu fairen Preisen. Wir verwenden ausschließlich hochwertige Materialien 
                    namhafter Hersteller und legen großen Wert auf eine professionelle Ausführung.
                  </p>
                  <h3 className="text-2xl font-bold mt-8 mb-4">Warum FarbWelt Service?</h3>
                  <ul>
                    <li><strong>Lokale Präsenz:</strong> Wir kennen {locationName} und sind schnell bei Ihnen vor Ort</li>
                    <li><strong>Faire Preise:</strong> Transparente Kostenvoranschläge ohne versteckte Kosten</li>
                    <li><strong>Qualitätsgarantie:</strong> Wir arbeiten bis Sie zufrieden sind</li>
                    <li><strong>Flexibilität:</strong> Termine auch kurzfristig möglich</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Districts Section (only for city pages without district) */}
        {!district && city.districts.length > 0 && (
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold font-barlow mb-4 text-center">
                Wir sind in ganz {city.name} für Sie da
              </h2>
              <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
                Klicken Sie auf einen Stadtteil, um mehr über unsere lokalen Malerleistungen zu erfahren.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {city.districts.map((d) => (
                  <Link
                    key={d.slug}
                    to={`/maler-${city.slug}-${d.slug}`}
                    className="p-4 bg-gray-50 border rounded-lg hover:border-[#9B1B30] hover:bg-white hover:shadow-md transition-all text-center group"
                    data-testid={`district-link-${d.slug}`}
                  >
                    <MapPin className="w-5 h-5 text-[#9B1B30] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                    <span className="font-medium">Maler {d.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Trust-Signale Sektion (nur für Hub-Seiten / Stadt ohne Stadtteil) */}
        {!district && (
          <section className="py-16 bg-gradient-to-br from-[#2E2A88]/5 to-[#9B1B30]/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
                  Warum FarbWelt Service in {city.name}?
                </h2>
                <div className="grid md:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-[#9B1B30]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Lokale Expertise seit Jahren</h3>
                        <p className="text-gray-600">
                          Wir kennen {city.name} und das Rheinland. Von der Altbauwohnung bis zum 
                          modernen Neubau – wir wissen, welche Materialien und Techniken hier funktionieren.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Star className="w-6 h-6 text-[#9B1B30]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Qualität ohne Kompromisse</h3>
                        <p className="text-gray-600">
                          Wir arbeiten ausschließlich mit Premium-Farben von Caparol, Brillux und 
                          Alpina. Denn gute Arbeit braucht gute Materialien.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-[#9B1B30]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Termingerecht & Zuverlässig</h3>
                        <p className="text-gray-600">
                          Wir halten unsere Termine ein. Pünktlich zum vereinbarten Tag sind 
                          wir fertig – ohne Verzögerungen, ohne Ausreden.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-md">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#9B1B30]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Users className="w-6 h-6 text-[#9B1B30]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2">Festpreise ohne Überraschungen</h3>
                        <p className="text-gray-600">
                          Sie bekommen einen transparenten Kostenvoranschlag. Was wir anbieten, 
                          das gilt – keine versteckten Kosten, keine Nachforderungen.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prozess-Visualisierung */}
                <div className="bg-white p-8 rounded-xl shadow-md">
                  <h3 className="font-bold text-xl mb-6 text-center">So einfach funktioniert's</h3>
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                    {[
                      { step: "1", title: "Anfrage", desc: "Online Termin buchen oder anrufen" },
                      { step: "2", title: "Beratung", desc: "Kostenlose Vor-Ort-Besichtigung" },
                      { step: "3", title: "Angebot", desc: "Transparenter Festpreis" },
                      { step: "4", title: "Ausführung", desc: "Professionelle Umsetzung" },
                      { step: "5", title: "Abnahme", desc: "Gemeinsame Endkontrolle" }
                    ].map((item, idx) => (
                      <div key={idx} className="flex flex-col items-center text-center flex-1">
                        <div className="w-10 h-10 bg-[#9B1B30] text-white rounded-full flex items-center justify-center font-bold mb-2">
                          {item.step}
                        </div>
                        <h4 className="font-bold text-sm">{item.title}</h4>
                        <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                        {idx < 4 && <ArrowRight className="w-4 h-4 text-gray-300 mt-2 hidden md:block rotate-0 md:rotate-0" />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Zurück zum Hub & Service-Links (für Stadtteil-Seiten) */}
        {district && (
          <section className="py-12 bg-white border-t">
            <div className="container mx-auto px-4">
              {/* Zurück zur Stadt-Hub-Seite */}
              <div className="mb-8">
                <Link 
                  to={`/maler-${city.slug}`}
                  className="inline-flex items-center text-[#9B1B30] font-medium hover:underline"
                  data-testid="back-to-hub-link"
                >
                  <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                  Alle Stadtteile in {city.name} ansehen
                </Link>
              </div>
              
              {/* Service-Links für diesen Standort */}
              <h3 className="text-xl font-bold mb-4">Unsere Leistungen in {locationName}:</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.values(services).map((service) => (
                  <Link
                    key={service.slug}
                    to={`/${service.slug}-${city.slug}`}
                    className="p-4 bg-gray-50 border rounded-lg hover:border-[#9B1B30] hover:bg-white hover:shadow-md transition-all group"
                    data-testid={`district-service-link-${service.slug}`}
                  >
                    <span className="font-medium text-gray-800 group-hover:text-[#9B1B30]">
                      {service.name} {city.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Nearby Areas (for district pages) */}
        {district && nearbyAreas.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="container mx-auto px-4">
              <h3 className="text-xl font-bold mb-4">Auch in der Nähe von {district.name}:</h3>
              <div className="flex flex-wrap gap-3">
                {nearbyAreas.map((area) => (
                  <Link
                    key={area.slug}
                    to={`/maler-${city.slug}-${area.slug}`}
                    className="px-4 py-2 bg-white border rounded-full hover:bg-[#9B1B30] hover:text-white hover:border-[#9B1B30] transition-colors text-sm"
                  >
                    Maler {area.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold font-barlow mb-8 text-center">
              Häufige Fragen zu Malerarbeiten in {locationName}
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqItems.map((faq, idx) => (
                <FaqItem 
                  key={idx}
                  question={faq.question}
                  answer={faq.answer}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#2E2A88] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-barlow mb-4">
              Bereit für Ihr Projekt in {locationName}?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Buchen Sie jetzt Ihren kostenlosen Besichtigungstermin. 
              Wir beraten Sie vor Ort und erstellen ein unverbindliches Angebot.
            </p>
            <Link to="/termin" data-testid="cta-termin-buchen">
              <Button size="lg" className="bg-[#9B1B30] hover:bg-[#7a1525] text-white px-12 py-6 text-lg">
                Jetzt Termin buchen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Internal Links - Other Cities */}
        <section className="py-12 border-t bg-white">
          <div className="container mx-auto px-4">
            <h3 className="text-lg font-bold mb-4">FarbWelt Service – Auch in Ihrer Nähe:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.values(cities).filter(c => c.slug !== city.slug).map((c) => (
                <Link
                  key={c.slug}
                  to={`/maler-${c.slug}`}
                  className="text-[#9B1B30] hover:underline"
                >
                  Maler {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Internal Links - Services */}
        <section className="py-8 border-t bg-gray-50">
          <div className="container mx-auto px-4">
            <h3 className="text-lg font-bold mb-4">Unsere Leistungen:</h3>
            <div className="flex flex-wrap gap-4">
              {Object.values(services).map((service) => (
                <Link
                  key={service.slug}
                  to={`/${service.slug}-${city.slug}`}
                  className="text-gray-600 hover:text-[#9B1B30]"
                >
                  {service.name} {city.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

// FAQ Item Component
function FaqItem({ question, answer }) {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <h3 className="font-bold text-lg mb-2 text-gray-900">{question}</h3>
      <p className="text-gray-600 leading-relaxed">{answer}</p>
    </div>
  );
}

// Default FAQ for locations without unique content
function getDefaultFaq(locationName, cityName) {
  return [
    {
      question: `Was kosten Malerarbeiten in ${locationName}?`,
      answer: `Die Kosten für Malerarbeiten hängen von verschiedenen Faktoren ab: Raumgröße, Zustand der Wände, gewünschte Farben und Techniken. Wir erstellen Ihnen gerne ein kostenloses und unverbindliches Angebot nach einer Vor-Ort-Besichtigung in ${locationName}.`
    },
    {
      question: `Wie schnell können Sie in ${locationName} einen Termin machen?`,
      answer: `In der Regel können wir innerhalb von 1-2 Wochen mit den Arbeiten beginnen. Für dringende Projekte bieten wir auch einen Express-Service an. Buchen Sie einfach online einen Besichtigungstermin.`
    },
    {
      question: `Arbeiten Sie auch am Wochenende in ${locationName}?`,
      answer: `Unsere regulären Arbeitszeiten sind Montag bis Freitag. In Ausnahmefällen und nach Absprache sind auch Wochenendarbeiten möglich.`
    },
    {
      question: `Bieten Sie auch Fassadenarbeiten in ${locationName} an?`,
      answer: `Ja, wir bieten das komplette Spektrum an Malerarbeiten: Innenanstrich, Fassadenanstrich, Tapezierarbeiten, Lackierarbeiten und mehr. Alle Leistungen sind in ${locationName} und Umgebung verfügbar.`
    }
  ];
}

// Default service highlights
function getDefaultServiceHighlights() {
  return [
    {
      title: "Innenanstrich",
      description: "Professionelles Streichen von Wänden und Decken mit hochwertigen Farben.",
      link: "/leistungen/innenanstrich"
    },
    {
      title: "Tapezierarbeiten",
      description: "Von Raufaser bis Designertapete – sauber und blasenfrei verarbeitet.",
      link: "/leistungen/tapezierarbeiten"
    },
    {
      title: "Wohnungsrenovierung",
      description: "Komplettrenovierung aus einer Hand – stressfrei und termingerecht.",
      link: "/leistungen/renovierung"
    }
  ];
}

// 404 Content
function NotFoundContent() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-4">
        <h1 className="text-4xl font-bold mb-4">Seite nicht gefunden</h1>
        <p className="text-gray-600 mb-8">Die gewünschte Seite existiert leider nicht.</p>
        <div className="space-x-4">
          <Link to="/">
            <Button>Zurück zur Startseite</Button>
          </Link>
          <Link to="/termin">
            <Button variant="outline">Termin buchen</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
